import React from "react";
import { Link } from "react-router-dom";
import "./styles/LegalPage.css";
import config from "../configs/config";

function TermsOfUse() {
  const redirectUrl = config.REDIRECT_URL || "https://pythontest.excavateservices.com";

  return (
    <div className="legal-page">
      <header className="legal-header">
        <span className="legal-header-brand">Excavate Research and Analysis Pvt Ltd</span>
        <Link to="/" className="legal-back-link">&#8592; Back to Login</Link>
      </header>

      <div className="legal-body">
        <h1 className="legal-doc-title">Terms of Use</h1>
        <p className="legal-doc-meta">Effective: 17 May 2026</p>

        <div className="legal-section">
          <h2>1. About these Terms</h2>
          <p>
            These Terms of Use ("Terms") govern access to and use of the websites, applications, portals,
            and other online services (collectively, the "Services") provided by Excavate Research and
            Analysis Pvt Ltd ("Excavate", "we", "us", or "our"), a company incorporated in India with its
            registered office at NDM1, Netaji Subhash Place, Delhi 110034, India, including the service
            available at{" "}
            <a href={redirectUrl} target="_blank" rel="noreferrer">
              {redirectUrl}
            </a>
            .
          </p>
          <p>
            By accessing or using the Services, including by accepting an invitation to access our Microsoft
            tenant via Single Sign-On, you agree to be bound by these Terms. If you do not agree, you must
            not access or use the Services.
          </p>
          <p>
            If you access the Services on behalf of an organisation (for example, as an employee or
            contractor of a client), you represent and warrant that you have authority to bind that
            organisation to these Terms.
          </p>
        </div>

        <div className="legal-section">
          <h2>2. Eligibility and accounts</h2>
          <ul>
            <li>The Services are intended for business users aged 18 or over. To use the Services you may be required to register or be invited as a user.</li>
            <li>You must provide accurate and current information when registering or accepting an invitation.</li>
            <li>You are responsible for maintaining the confidentiality of your sign-in credentials and for all activities that occur under your account.</li>
            <li>You must notify us promptly at <a href="mailto:privacy@excavateservices.com">privacy@excavateservices.com</a> of any suspected unauthorised access or use.</li>
            <li>We may suspend or terminate access where we reasonably believe an account is being misused or where required by law or contract.</li>
          </ul>
        </div>

        <div className="legal-section">
          <h2>3. Single Sign-On (Microsoft Entra)</h2>
          <p>We use Microsoft Entra (Azure Active Directory) for authentication. Where you sign in via SSO:</p>
          <ul>
            <li>You authenticate with your own organisation's identity provider; we do not receive your password.</li>
            <li>With your consent on the Microsoft permissions screen, Microsoft shares limited profile information (name, work email, photo) and standard tenant audit logging with us. See our <Link to="/privacy">Privacy Policy</Link> for details.</li>
            <li>By accepting the Microsoft consent screen, you authorise this limited sharing. You can review or revoke organisational access at any time at <a href="https://myaccount.microsoft.com/organizations" target="_blank" rel="noreferrer">https://myaccount.microsoft.com/organizations</a>.</li>
            <li>Your underlying identity is managed by your own organisation. We do not control or have visibility into your home tenant's password, mailbox, files, or other data.</li>
          </ul>
        </div>

        <div className="legal-section">
          <h2>4. Acceptable use</h2>
          <p>You agree not to:</p>
          <ul>
            <li>Use the Services for any unlawful, fraudulent, or harmful purpose;</li>
            <li>Attempt to gain unauthorised access to any part of the Services, other users' accounts, or any systems or networks connected to the Services;</li>
            <li>Probe, scan, or test the vulnerability of the Services or breach any security or authentication measures, except with our prior written consent;</li>
            <li>Interfere with, disrupt, or place an unreasonable load on the Services (including by automated scraping, denial-of-service techniques, or bots);</li>
            <li>Upload or transmit viruses, malware, or other malicious code;</li>
            <li>Reverse engineer, decompile, or disassemble any part of the Services, except to the extent expressly permitted by law;</li>
            <li>Use the Services to send spam or unsolicited communications;</li>
            <li>Misrepresent your identity or affiliation, or impersonate any person or entity;</li>
            <li>Use the Services in a way that infringes the intellectual property, privacy, or other rights of any third party.</li>
          </ul>
        </div>

        <div className="legal-section">
          <h2>5. Client data and confidentiality</h2>
          <p>
            Through the Services, you may access data belonging to Excavate or our clients, including survey
            data, study designs, tabulation specifications, banners, questionnaires, and reports ("Project Data").
          </p>
          <ul>
            <li>Project Data is confidential and may include personal data of survey respondents and commercially sensitive information.</li>
            <li>You may use Project Data solely for the purpose for which access was granted and in accordance with any project-specific instructions.</li>
            <li>You must not copy, download, share, or disclose Project Data outside your organisation except as expressly permitted by the relevant agreement with us or our client.</li>
            <li>On termination of access or completion of the project, you must, at our request, delete or return any Project Data within your control.</li>
          </ul>
        </div>

        <div className="legal-section">
          <h2>6. Intellectual property</h2>
          <p>
            The Services, including all software, databases, documentation, designs, logos, and content
            (other than Project Data and content you provide), are owned by or licensed to Excavate and are
            protected by applicable intellectual property laws.
          </p>
          <ul>
            <li>We grant you a limited, non-exclusive, non-transferable, revocable licence to access and use the Services for the permitted purpose during the period of your authorised access.</li>
            <li>Except as expressly permitted, you may not copy, modify, distribute, sell, sublicense, or create derivative works of the Services.</li>
            <li>Feedback you provide about the Services may be used by us without obligation or compensation, provided we do not identify you publicly without consent.</li>
          </ul>
        </div>

        <div className="legal-section">
          <h2>7. Third-party services</h2>
          <p>
            The Services may interoperate with or link to third-party services, including Microsoft Entra,
            Microsoft 365, analytics providers, and cloud infrastructure. Your use of those services is
            governed by their own terms and privacy notices. We are not responsible for third-party
            services, but we contract with material sub-processors under appropriate confidentiality and
            security commitments.
          </p>
        </div>

        <div className="legal-section">
          <h2>8. Availability and changes</h2>
          <ul>
            <li>We aim to keep the Services available but do not guarantee uninterrupted or error-free access.</li>
            <li>We may suspend the Services for maintenance, security, or compliance reasons, with reasonable notice where practicable.</li>
            <li>We may modify or discontinue features of the Services. Where a change materially reduces functionality available to you under a paid contract, the terms of that contract control.</li>
          </ul>
        </div>

        <div className="legal-section">
          <h2>9. Disclaimers</h2>
          <p>
            To the maximum extent permitted by applicable law, the Services are provided "as is" and "as
            available". We disclaim all warranties, express or implied, including warranties of
            merchantability, fitness for a particular purpose, non-infringement, and quiet enjoyment. We do
            not warrant that the Services will meet your requirements, be uninterrupted, secure, or free of
            errors, or that defects will be corrected.
          </p>
          <p>
            Nothing in these Terms excludes or limits any rights you have under applicable law that cannot
            be excluded or limited (for example, consumer guarantees under the Australian Consumer Law).
          </p>
        </div>

        <div className="legal-section">
          <h2>10. Limitation of liability</h2>
          <p>To the maximum extent permitted by law:</p>
          <ul>
            <li>Neither party will be liable for indirect, incidental, special, consequential, or punitive damages, or for loss of profits, revenue, goodwill, or data, even if advised of the possibility of such damages;</li>
            <li>Our total aggregate liability arising out of or relating to the Services or these Terms is limited to the amount paid by you to us for the Services in the 12 months preceding the event giving rise to the claim, or, where no fees have been paid, INR 10,000 (or local equivalent).</li>
            <li>Where the Services are provided under a separate written agreement with your organisation, the liability provisions of that agreement control over this Section to the extent of any conflict.</li>
          </ul>
        </div>

        <div className="legal-section">
          <h2>11. Indemnity</h2>
          <p>
            You agree to indemnify and hold harmless Excavate, its affiliates, and their respective
            officers, employees, and agents from and against any claims, damages, losses, and reasonable
            legal fees arising from (a) your breach of these Terms, (b) your misuse of the Services, or (c)
            your violation of any law or the rights of a third party in connection with your use of the
            Services.
          </p>
        </div>

        <div className="legal-section">
          <h2>12. Suspension and termination</h2>
          <ul>
            <li>You may stop using the Services at any time. If your access was granted under an agreement with your organisation, termination is governed by that agreement.</li>
            <li>We may suspend or terminate your access immediately if you breach these Terms, if required by law, or to protect the security of the Services or other users.</li>
            <li>Sections that by their nature should survive termination (including confidentiality, intellectual property, disclaimers, limitation of liability, indemnity, and governing law) will continue to apply after termination.</li>
          </ul>
        </div>

        <div className="legal-section">
          <h2>13. Governing law and disputes</h2>
          <p>
            These Terms are governed by the laws of India, without regard to its conflict-of-laws
            principles. Subject to applicable mandatory law, the courts of Delhi, India shall have exclusive
            jurisdiction over any disputes arising out of or relating to these Terms or the Services.
          </p>
          <p>
            Nothing in this Section limits any non-waivable right you may have under your local law to bring
            proceedings in the courts of your country of residence (for example, consumer protection rights
            in Australia, the UK, the EEA, or Canada).
          </p>
        </div>

        <div className="legal-section">
          <h2>14. Changes to these Terms</h2>
          <p>
            We may update these Terms from time to time. We will post the updated version at{" "}
            <a href={redirectUrl} target="_blank" rel="noreferrer">
              {redirectUrl}
            </a>{" "}
            and update the "Effective" date above. For material changes, we will provide reasonable advance
            notice where practicable (for example, by email or in-product notification). Your continued use
            of the Services after the updated Terms take effect constitutes acceptance.
          </p>
        </div>

        <div className="legal-section">
          <h2>15. Contact</h2>
          <p>If you have questions about these Terms, please contact us:</p>
          <div className="legal-contact-block">
            Excavate Research and Analysis Pvt Ltd{"\n"}
            NDM1, Netaji Subhash Place, Delhi 110034, India{"\n"}
            Email: <a href="mailto:privacy@excavateservices.com">privacy@excavateservices.com</a>
          </div>
        </div>
      </div>
    </div>
  );
}

export default TermsOfUse;